import React from 'react';
import { View } from 'react-native';
import { GLView } from 'expo-gl';
import {
  Scene,
  GridHelper,
  PerspectiveCamera,
  WebGLRenderer,
  DirectionalLight,
  AmbientLight,
  PointsMaterial,
  Points,
  MeshPhysicalMaterial,
  Mesh,
  LineSegments
} from 'three';
import { PLYLoader } from "three/examples/jsm/loaders/PLYLoader";
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import ScannerCoreService from '../../shared/services/core-services';
import { useGlobalState } from '../../shared/state';

type Props = {
  leftPLY?: string,
  leftSole?: string,
  rightPLY?: string,
  rightSole?: string,
  email?: string,
  viewMode?: "top" | "front" | "side",
  maxDistance?: number
}

const ThreeStage = ({ leftPLY, leftSole, rightPLY, rightSole, email, viewMode, maxDistance }: Props): JSX.Element => {
  const [userToken] = useGlobalState('userToken');
  const [currentScanner] = useGlobalState('currentScanner');

  // const viewMode: 'top' | 'front' | 'side' = 'top';

  function onContextCreate(gl) {
    const scene = new Scene();
    const camera = new PerspectiveCamera(55, gl.drawingBufferWidth / gl.drawingBufferHeight, 0.5, 1500);

    const renderer = new WebGLRenderer({ antialias: true, alpha: true, canvas: gl.canvas });
    renderer.setSize(gl.drawingBufferWidth, gl.drawingBufferHeight);
    renderer.setClearColor(0xffffff, 0);

    // ---------- CAMERA CONTROL ----------
    const setCameraView = (camera, controls, mode) => {
      switch (mode) {
        case 'top':
          camera.position.set(0, 650, 50);
          break;

        case 'front':
          camera.position.set(0, 0, 700);
          break;

        case 'side':
          camera.position.set(700, 0, 0);
          break;

        default:
          camera.position.set(100, 100, 700);
      }

      controls.target.set(0, 0, 0);
      controls.update();
    };
    // -----------------------------------

    let gridPlane = new GridHelper(500, 10);
    gridPlane.position.y = -101;
    scene.add(gridPlane);

    const distance = 3000;

    const light1 = new DirectionalLight(0xffffff, 1.5);
    light1.position.set(0, 0, distance);

    const light2 = new DirectionalLight(0xffffff, 1.5);
    light2.position.set(0, 0, -distance);

    const light3 = new DirectionalLight(0xffffff, 1.5);
    light3.position.set(0, distance, 0);

    const light4 = new DirectionalLight(0xffffff, 1.5);
    light4.position.set(0, -distance, 0);

    const ambientLight = new AmbientLight(0x808080, 1);

    scene.add(light1, light2, light3, light4, ambientLight);

    const loader = new PLYLoader();

    LoadPLY(scene, loader, 'left', leftSole, leftPLY);
    LoadPLY(scene, loader, 'right', rightSole, rightPLY);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.25;
    controls.screenSpacePanning = false;
    controls.enableZoom = true;
    controls.minDistance = 450;
    controls.maxDistance = maxDistance || 950;

    // 👉 Apply selected view
    setCameraView(camera, controls, viewMode);

    const render = () => {
      requestAnimationFrame(render);
      controls.update();
      renderer.render(scene, camera);
      gl.endFrameEXP();
    };

    render();
  }

  const LoadPLY = (scene, loader, side, soleURL, MeshURL) => {
    if (MeshURL) {
      loader.load(MeshURL, (Meshgeometry) => {
        Meshgeometry.computeVertexNormals();
        addMesh(scene, Meshgeometry, side, 'unprocessed');

        if (soleURL) {
          loader.load(soleURL, (Solegeometry) => {
            Solegeometry.computeVertexNormals();
            addSole(scene, Solegeometry, side);
          });
        }
      });
    } else if (email) {
      ScannerCoreService.fetchUserMesh(email, side, 'standing', currentScanner['scanner-token'], userToken)
        .then((data) => {
          loader.load(data.original_data_url, (Meshgeometry) => {
            Meshgeometry.computeVertexNormals();
            addMesh(scene, Meshgeometry, side, 'unprocessed');

            if (currentScanner?.['scanner-version'] && soleURL) {
              loader.load(soleURL, (Solegeometry) => {
                Solegeometry.computeVertexNormals();
                addSole(scene, Solegeometry, side);
              });
            }
          });
        })
        .catch(error => console.log('ERROR LoadPLY: ', error));
    }
  };

  const addSole = (scene, Solegeometry: any, side: string) => {
    const material = new PointsMaterial({
      size: 8,
      fog: true,
      vertexColors: true,
    });

    const meshSole = new Points(Solegeometry, material);

    if (currentScanner?.['scanner-version'] === 'new' || currentScanner?.['scanner-version'] === 'xpod') {
      if (currentScanner?.['scanner-type'] === 'multi' && currentScanner['scanner-version'] !== 'xpod') {
        meshSole.position.set(side === 'left' ? -16 : 16, -101.5, 3);
      } else {
        meshSole.position.set(side === 'left' ? 100 : -100, -101.5, -130);
      }
    } else {
      meshSole.position.set(side === 'left' ? -100 : 100, -101.5, 0);
    }

    meshSole.scale.multiplyScalar(1.3);
    scene.add(meshSole);
  };

  const addMesh = (scene, Meshgeometry: any, side: string, status: string) => {
    const material = new MeshPhysicalMaterial({
      color: "#ffffff",
      metalness: 0.3,
      roughness: 0.7,
      reflectivity: 1,
      opacity: 1
    });

    const mesh = new Mesh(Meshgeometry, material);

    if (status === 'unprocessed') {
      if (currentScanner?.['scanner-version'] === 'new' || currentScanner?.['scanner-version'] === 'xpod') {
        if (currentScanner?.['scanner-type'] === 'multi' && currentScanner['scanner-version'] !== 'xpod') {
          mesh.position.x = side === 'left' ? -30 : 30;
        } else {
          mesh.position.set(side === 'left' ? 100 : -100, -100, -150);
        }
      } else {
        mesh.position.set(side === 'left' ? -100 : 100, -100, 0);
      }
    } else {
      mesh.position.set(side === 'left' ? -100 : 100, -100, -30);
      mesh.rotateX(-Math.PI / 2);
      mesh.rotateZ(-Math.PI / 2);
    }

    mesh.scale.multiplyScalar(1.4);

    const wireframe = new LineSegments(
      Meshgeometry,
      new MeshPhysicalMaterial({
        color: "#ffffff",
        wireframe: true,
      })
    );

    mesh.add(wireframe);
    scene.add(mesh);
  };

  return (
    <View style={{ flex: 1 }}>
      <GLView style={{ width: "100%", height: "100%" }} onContextCreate={onContextCreate} />
    </View>
  );
};

export default ThreeStage;